import { SavedScope } from '@shared/models/saved-scope.model';
import { BUILTIN_SCOPE_IDS } from '@shared/models/scope-preset.model';
import { savedScopeToPreset } from '@features/lifecycles/saved-scopes.helpers';
import {
  getLocalStorageJSON,
  setLocalStorageJSON,
  createLocalStorageRecordStore,
} from '@client/helpers/local-storage.helpers';
import {
  ScopeDimensions,
  ScopeState,
} from '@shared/models/scope-selection.model';
import { Lifecycle } from '@shared/models/lifecycles.model';
import { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
export type {
  ScopeDimensions,
  ScopeState,
} from '@shared/models/scope-selection.model';

/** localStorage key for the consolidated overview scope. */
export const SCOPE_STORAGE_KEY = 'scope';

/** localStorage key for per-project applied scopes. */
export const PROJECT_APPLIED_SCOPE_KEY = 'project-applied-scope';

const DEFAULT_DIMENSIONS: ScopeDimensions = {
  lifecycles: [Lifecycle.A1A3, Lifecycle.A4, Lifecycle.A5], // A1-A5
  sbefGroups: [],
  conservative: false,
};

export const DEFAULT_SCOPE: ScopeState = {
  kind: 'custom',
  ...DEFAULT_DIMENSIONS,
};

/** Keys the scope dimensions were persisted under before they shared one object. */
const LEGACY_KEYS = [
  'visible_lifecycles',
  'visible_sbef_groups',
  'conservative_generic_carbon',
] as const;

/**
 * Fold a pre-consolidation scope into one custom scope so upgrading does not
 * reset a manual selection, then drop the old keys. Missing keys keep
 * {@link DEFAULT_SCOPE} dimensions.
 */
const migrateLegacyScope = (): ScopeState => {
  const [lifecyclesKey, sbefGroupsKey, conservativeKey] = LEGACY_KEYS;
  const lifecycles = getLocalStorageJSON<Lifecycle[]>(lifecyclesKey);
  const sbefGroups = getLocalStorageJSON<SbefScopeGroupKey[]>(sbefGroupsKey);
  const conservative = getLocalStorageJSON<boolean>(conservativeKey);
  LEGACY_KEYS.forEach((key) => setLocalStorageJSON(key, undefined));
  return {
    kind: 'custom',
    lifecycles: lifecycles ?? DEFAULT_DIMENSIONS.lifecycles,
    sbefGroups: sbefGroups ?? DEFAULT_DIMENSIONS.sbefGroups,
    conservative: conservative ?? DEFAULT_DIMENSIONS.conservative,
  };
};

/**
 * Value to seed the scope store with. A consolidated `scope` key wins; otherwise
 * leftover pre-consolidation keys are folded into one custom scope.
 */
export const getInitialScopeState = (): ScopeState => {
  const existing = getLocalStorageJSON<ScopeState>(SCOPE_STORAGE_KEY);
  const migrated = migrateLegacyScope();
  return existing ?? migrated;
};

/** Per-project applied scope storage (local, per user, per browser). */
const projectAppliedScopeStore = createLocalStorageRecordStore<
  number,
  ScopeState
>(PROJECT_APPLIED_SCOPE_KEY);

/**
 * Get the applied scope for a project, falling back to the first enabled scope
 * if the stored one is not enabled, or to the default scope if none are enabled.
 * Does not persist the fallback; the project context saves the initial selection.
 */
export const getProjectAppliedScope = (
  projectId: number,
  enabledIds: string[],
  savedScopes: SavedScope[] = [],
  organization?: string,
): ScopeState => {
  const stored = projectAppliedScopeStore.getItem(projectId);

  const scope =
    stored &&
    isScopeEnabled(stored, enabledIds) &&
    !(stored.kind === 'preset' && stored.presetId.startsWith('saved:'))
      ? stored
      : getFirstEnabledScope(enabledIds, savedScopes);
  if (scope.kind !== 'preset' || !organization) return scope;
  const saved = savedScopes.find(({ id }) => `saved:${id}` === scope.presetId);
  if (!saved) return scope;
  return {
    kind: 'saved',
    organization,
    preset: { ...savedScopeToPreset(saved), id: `saved:${saved.id}` },
  };
};

/**
 * Set the applied scope for a project.
 */
export const setProjectAppliedScope = (
  projectId: number,
  scope: ScopeState,
): void => {
  projectAppliedScopeStore.setItem(projectId, scope);
};

/**
 * Get the first enabled scope from the list of enabled IDs.
 * Built-in presets precede saved scopes, matching the selector's display order.
 * Saved definitions follow their organization catalog order. Before that catalog
 * loads, unresolved IDs retain their enabled-list order. Returns DEFAULT_SCOPE
 * if none are enabled.
 */
export const getFirstEnabledScope = (
  enabledIds: string[],
  savedScopes: SavedScope[] = [],
): ScopeState => {
  const firstEnabledId =
    BUILTIN_SCOPE_IDS.find((id) => enabledIds.includes(id)) ??
    savedScopes
      .map(({ id }) => `saved:${id}`)
      .find((id) => enabledIds.includes(id)) ??
    enabledIds[0];
  return firstEnabledId
    ? { kind: 'preset', presetId: firstEnabledId }
    : DEFAULT_SCOPE;
};

/**
 * Get the applied scope for the overview/project list, with fallback to first enabled.
 * @param enabledIds List of enabled scope IDs for the organization
 * @param stored Currently stored overview scope from localStorage
 */
export const getOverviewAppliedScope = (
  enabledIds: string[],
  stored?: ScopeState,
): ScopeState => {
  // Check if stored scope is still enabled
  if (stored && isScopeEnabled(stored, enabledIds)) {
    return stored;
  }

  // Fallback to first enabled scope
  return getFirstEnabledScope(enabledIds);
};

/**
 * Check if a scope state is in the enabled list.
 */
const isScopeEnabled = (scope: ScopeState, enabledIds: string[]): boolean => {
  switch (scope.kind) {
    case 'preset':
      return enabledIds.includes(scope.presetId);
    case 'saved':
      return enabledIds.includes(scope.preset.id);
    case 'custom':
      // Custom scopes are always "enabled" if there are any enabled scopes
      return enabledIds.length > 0;
    default:
      return false;
  }
};
